/**
 * Parse the per-person values JSON.
 *
 * A plain function rather than a `useMemo`: the work is trivial, the React Compiler
 * memoises it anyway, and this way it is unit-testable — which it needs to be, since
 * it makes two real decisions about malformed input.
 *
 * Scalars are coerced to strings so a numeric rank in the JSON is usable. Nested
 * objects are refused rather than coerced, because `String({})` renders
 * "[object Object]" into a message going to sixty people.
 */
export function parseValues(text: string): {
  values: Record<string, Record<string, string>>;
  error: string;
} {
  if (!text.trim()) return { values: {}, error: "" };

  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (caught) {
    return {
      values: {},
      error: caught instanceof Error ? caught.message : "Invalid JSON.",
    };
  }

  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return { values: {}, error: "Expected an object keyed by email address." };
  }

  const values: Record<string, Record<string, string>> = {};
  for (const [email, fields] of Object.entries(raw)) {
    if (fields === null || typeof fields !== "object" || Array.isArray(fields)) {
      return {
        values: {},
        error: `"${email}" should map to an object of variables, e.g. {"grade": "A"}.`,
      };
    }
    const flat: Record<string, string> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (value !== null && typeof value === "object") {
        return {
          values: {},
          error: `"${email}" → "${key}" is nested. Only flat values render.`,
        };
      }
      flat[key] = String(value);
    }
    values[email] = flat;
  }
  return { values, error: "" };
}
